import React, { useMemo, useState } from 'react';
import {
  Document as DocxDocument,
  Packer,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  TextRun,
  HeadingLevel,
  WidthType,
  AlignmentType,
} from 'docx';
import { InsuranceSummaryRecord } from './InsuranceSummaryView';
import PdfViewer from './PdfViewer';

interface Citation {
  docId: string;
  docName: string;
  page: string;
  section?: string;
  quote?: string;
}

interface LabeledValue {
  label: string;
  value: string;
}

interface LineOfCoverage {
  line: string;
  carrier?: string;
  policy_number?: string;
  effective_date?: string;
  expiration_date?: string;
  premium?: number;
  commission?: number;
  limits?: LabeledValue[];
  deductibles?: LabeledValue[];
  key_endorsements?: string[];
  notable_exclusions?: string[];
  source_documents?: string[];
}

interface ParsedSummary {
  named_insured?: string;
  doi_address?: string;
  summary_period?: { start?: string; end?: string };
  lines_of_coverage?: LineOfCoverage[];
  total_premium?: number;
  narrative?: string;
  citations?: Citation[];
}

interface Props {
  summary: InsuranceSummaryRecord;
  customerName: string;
  onClose: () => void;
}

const apiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatCurrency = (n: number | undefined): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

const formatDate = (s: string | undefined): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return s;
};

const stripInlineCites = (s: string): string => s.replace(/\[CITE:\s*"[^"]+"\]/g, '').replace(/\s{2,}/g, ' ').trim();

const buildDocx = async (parsed: ParsedSummary, customerName: string, title: string): Promise<Blob> => {
  const children: any[] = [];

  children.push(
    new Paragraph({
      text: title,
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.LEFT,
    })
  );

  const addLabelValue = (label: string, value: string) => {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${label}: `, bold: true }),
          new TextRun({ text: value }),
        ],
      })
    );
  };

  addLabelValue('Named Insured', parsed.named_insured || customerName);
  if (parsed.doi_address) addLabelValue('Mailing Address', parsed.doi_address);
  if (parsed.summary_period?.start || parsed.summary_period?.end) {
    addLabelValue('Summary Period', `${formatDate(parsed.summary_period?.start)} – ${formatDate(parsed.summary_period?.end)}`);
  }
  if (parsed.total_premium !== undefined) {
    addLabelValue('Total Annual Premium', formatCurrency(parsed.total_premium));
  }

  if (parsed.narrative) {
    children.push(new Paragraph({ text: '' }));
    children.push(new Paragraph({ text: 'Program Overview', heading: HeadingLevel.HEADING_1 }));
    stripInlineCites(parsed.narrative)
      .split(/\n{2,}|\r\n{2,}/)
      .filter((p) => p.trim().length > 0)
      .forEach((para) => children.push(new Paragraph({ text: para.trim() })));
  }

  children.push(new Paragraph({ text: '' }));
  children.push(new Paragraph({ text: 'Lines of Coverage', heading: HeadingLevel.HEADING_1 }));

  (parsed.lines_of_coverage || []).forEach((line, idx) => {
    children.push(new Paragraph({ text: `${idx + 1}. ${line.line}`, heading: HeadingLevel.HEADING_2 }));

    if (line.carrier) addLabelValue('Carrier', line.carrier);
    if (line.policy_number) addLabelValue('Policy #', line.policy_number);
    if (line.effective_date || line.expiration_date) {
      addLabelValue('Term', `${formatDate(line.effective_date)} – ${formatDate(line.expiration_date)}`);
    }
    if (line.premium !== undefined) addLabelValue('Premium', formatCurrency(line.premium));
    if (line.commission !== undefined) addLabelValue('Commission', formatCurrency(line.commission));

    const makeTable = (heading: string, rows: LabeledValue[], colA: string, colB: string) => {
      children.push(new Paragraph({ text: '' }));
      children.push(new Paragraph({ children: [new TextRun({ text: heading, bold: true })] }));
      const table = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            tableHeader: true,
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: colA, bold: true })] })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: colB, bold: true })] })] }),
            ],
          }),
          ...rows.map(
            (r) =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph(r.label)] }),
                  new TableCell({ children: [new Paragraph(r.value)] }),
                ],
              })
          ),
        ],
      });
      children.push(table);
    };

    if (line.limits?.length) makeTable('Limits', line.limits, 'Coverage', 'Limit');
    if (line.deductibles?.length) makeTable('Deductibles', line.deductibles, 'Type', 'Amount');

    if (line.key_endorsements?.length) {
      children.push(new Paragraph({ text: '' }));
      children.push(new Paragraph({ children: [new TextRun({ text: 'Key Endorsements', bold: true })] }));
      line.key_endorsements.forEach((e) => children.push(new Paragraph({ text: e, bullet: { level: 0 } })));
    }

    if (line.notable_exclusions?.length) {
      children.push(new Paragraph({ text: '' }));
      children.push(new Paragraph({ children: [new TextRun({ text: 'Notable Exclusions', bold: true })] }));
      line.notable_exclusions.forEach((e) => children.push(new Paragraph({ text: e, bullet: { level: 0 } })));
    }

    if (line.source_documents?.length) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `Source: ${line.source_documents.join(', ')}`, italics: true, size: 18 })],
        })
      );
    }

    children.push(new Paragraph({ text: '' }));
  });

  const doc = new DocxDocument({
    sections: [{ properties: {}, children }],
  });

  return Packer.toBlob(doc);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const safeFilename = (s: string, ext: string): string =>
  `${s.replace(/[^a-z0-9\-_. ]/gi, '_')}.${ext}`;

const InsuranceSummaryDetail: React.FC<Props> = ({ summary, customerName, onClose }) => {
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [citationsOpen, setCitationsOpen] = useState(false);
  const [citationViewer, setCitationViewer] = useState<{ documentId: string; page: number } | null>(null);

  const parsed: ParsedSummary | null = useMemo(() => {
    try {
      return typeof summary.content === 'string' ? JSON.parse(summary.content) : summary.content;
    } catch {
      return null;
    }
  }, [summary.content]);

  // Map docName → documentId using report-level arrays, as a fallback when
  // citation.docId isn't a UUID (e.g. AI returned doc_1 and post-processing
  // didn't replace it for some reason).
  const docNameToId = useMemo(() => {
    const map: Record<string, string> = {};
    if (summary.documentNames && summary.documentIds) {
      summary.documentNames.forEach((name, i) => {
        const id = summary.documentIds[i];
        if (name && id) map[name] = id;
      });
    }
    return map;
  }, [summary.documentNames, summary.documentIds]);

  const resolveCitationDoc = (c: Citation): { documentId: string; page: number } | null => {
    let id: string | null = null;
    if (c.docId && UUID_RE.test(c.docId)) id = c.docId;
    else if (c.docName && docNameToId[c.docName]) id = docNameToId[c.docName];
    if (!id) return null;
    const pageNum = parseInt((c.page || '').toString().replace(/[^\d]/g, ''), 10);
    return { documentId: id, page: Number.isFinite(pageNum) && pageNum > 0 ? pageNum : 1 };
  };

  const handleExportPdf = async () => {
    if (!parsed) return;
    setExporting('pdf');
    setExportError(null);
    setExportMenuOpen(false);
    try {
      const token = localStorage.getItem('riskninja-token');
      // Send the STRUCTURED summary so the backend can render a clean, branded
      // layout (compact limit grids, currency-formatted values) instead of a
      // lossy markdown round-trip.
      const res = await fetch(`${apiBase}/api/workflows/reports/insurance-summary-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          summary: parsed,
          title: summary.title,
          customerName,
        }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`PDF export failed (${res.status}) ${errText.slice(0, 200)}`);
      }
      const data = await res.json();
      if (!data?.success || !data?.pdfBase64) throw new Error('PDF export returned no data');

      const byteArray = Uint8Array.from(atob(data.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      downloadBlob(blob, safeFilename(summary.title, 'pdf'));
    } catch (err) {
      console.error(err);
      setExportError(err instanceof Error ? err.message : 'PDF export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleExportDocx = async () => {
    if (!parsed) return;
    setExporting('docx');
    setExportError(null);
    setExportMenuOpen(false);
    try {
      const blob = await buildDocx(parsed, customerName, summary.title);
      downloadBlob(blob, safeFilename(summary.title, 'docx'));
    } catch (err) {
      console.error(err);
      setExportError(err instanceof Error ? err.message : 'DOCX export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleCitationClick = (c: Citation) => {
    const resolved = resolveCitationDoc(c);
    if (!resolved) {
      setExportError(`Could not open source: "${c.docName}" — document may no longer be accessible.`);
      return;
    }
    setCitationViewer(resolved);
  };

  const citations = parsed?.citations || [];
  const citationOpenable = citations.filter((c) => resolveCitationDoc(c) !== null).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-secondary dark:text-dark-text truncate">
              {summary.title}
            </h2>
            <p className="text-sm text-accent dark:text-dark-muted">
              {new Date(summary.createdAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <div className="relative">
              <button
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={exporting !== null || !parsed}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-1"
              >
                {exporting === 'pdf' ? 'Generating PDF…' : exporting === 'docx' ? 'Generating DOCX…' : 'Export'}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7,10L12,15L17,10H7Z" />
                </svg>
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                  <button
                    onClick={handleExportPdf}
                    className="w-full text-left px-4 py-2 text-sm text-secondary dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Export as PDF
                  </button>
                  <button
                    onClick={handleExportDocx}
                    className="w-full text-left px-4 py-2 text-sm text-secondary dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Export as Word (.docx)
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          </div>
        </div>

        {exportError && (
          <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-6 py-2 text-sm">
            {exportError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!parsed ? (
            <div className="text-center py-12 text-accent dark:text-dark-muted">
              Unable to parse this summary's contents.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header card */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">
                      Named Insured
                    </div>
                    <div className="font-medium text-secondary dark:text-dark-text">
                      {parsed.named_insured || customerName}
                    </div>
                  </div>
                  {parsed.doi_address && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">
                        Mailing Address
                      </div>
                      <div className="font-medium text-secondary dark:text-dark-text">
                        {parsed.doi_address}
                      </div>
                    </div>
                  )}
                  {(parsed.summary_period?.start || parsed.summary_period?.end) && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">
                        Summary Period
                      </div>
                      <div className="font-medium text-secondary dark:text-dark-text">
                        {formatDate(parsed.summary_period?.start)} – {formatDate(parsed.summary_period?.end)}
                      </div>
                    </div>
                  )}
                  {parsed.total_premium !== undefined && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">
                        Total Annual Premium
                      </div>
                      <div className="font-semibold text-xl text-primary">
                        {formatCurrency(parsed.total_premium)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Narrative */}
              {parsed.narrative && (
                <div>
                  <h3 className="text-base font-semibold text-secondary dark:text-dark-text mb-2">
                    Program Overview
                  </h3>
                  <div className="text-sm text-secondary dark:text-dark-text whitespace-pre-wrap leading-relaxed">
                    {stripInlineCites(parsed.narrative)}
                  </div>
                </div>
              )}

              {/* Lines of coverage */}
              {parsed.lines_of_coverage?.length ? (
                <div>
                  <h3 className="text-base font-semibold text-secondary dark:text-dark-text mb-3">
                    Lines of Coverage ({parsed.lines_of_coverage.length})
                  </h3>
                  <div className="space-y-4">
                    {parsed.lines_of_coverage.map((line, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <h4 className="font-semibold text-secondary dark:text-dark-text">
                            {line.line}
                          </h4>
                          {line.premium !== undefined && (
                            <span className="text-sm font-medium text-primary">
                              {formatCurrency(line.premium)}
                            </span>
                          )}
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                            {line.carrier && (
                              <div>
                                <div className="uppercase tracking-wide text-accent dark:text-dark-muted">Carrier</div>
                                <div className="text-secondary dark:text-dark-text">{line.carrier}</div>
                              </div>
                            )}
                            {line.policy_number && (
                              <div>
                                <div className="uppercase tracking-wide text-accent dark:text-dark-muted">Policy #</div>
                                <div className="text-secondary dark:text-dark-text">{line.policy_number}</div>
                              </div>
                            )}
                            {line.effective_date && (
                              <div>
                                <div className="uppercase tracking-wide text-accent dark:text-dark-muted">Effective</div>
                                <div className="text-secondary dark:text-dark-text">{formatDate(line.effective_date)}</div>
                              </div>
                            )}
                            {line.expiration_date && (
                              <div>
                                <div className="uppercase tracking-wide text-accent dark:text-dark-muted">Expiration</div>
                                <div className="text-secondary dark:text-dark-text">{formatDate(line.expiration_date)}</div>
                              </div>
                            )}
                          </div>

                          {line.limits?.length ? (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">Limits</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                {line.limits.map((l, i) => (
                                  <div key={i} className="flex justify-between border-b border-gray-100 dark:border-gray-800 py-1">
                                    <span className="text-accent dark:text-dark-muted">{l.label}</span>
                                    <span className="text-secondary dark:text-dark-text font-medium">{l.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          {line.deductibles?.length ? (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">Deductibles</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                {line.deductibles.map((d, i) => (
                                  <div key={i} className="flex justify-between border-b border-gray-100 dark:border-gray-800 py-1">
                                    <span className="text-accent dark:text-dark-muted">{d.label}</span>
                                    <span className="text-secondary dark:text-dark-text font-medium">{d.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          {line.key_endorsements?.length ? (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">Key Endorsements</div>
                              <ul className="list-disc list-inside text-sm text-secondary dark:text-dark-text space-y-0.5">
                                {line.key_endorsements.map((e, i) => (<li key={i}>{e}</li>))}
                              </ul>
                            </div>
                          ) : null}

                          {line.notable_exclusions?.length ? (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-accent dark:text-dark-muted mb-1">Notable Exclusions</div>
                              <ul className="list-disc list-inside text-sm text-secondary dark:text-dark-text space-y-0.5">
                                {line.notable_exclusions.map((e, i) => (<li key={i}>{e}</li>))}
                              </ul>
                            </div>
                          ) : null}

                          {line.source_documents?.length ? (
                            <div className="text-xs text-accent dark:text-dark-muted">
                              Source: {line.source_documents.join(', ')}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Citations — hidden by default */}
              {citations.length > 0 && (
                <div>
                  <button
                    onClick={() => setCitationsOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{ transform: citationsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}
                    >
                      <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
                    </svg>
                    {citationsOpen ? 'Hide' : 'Show'} citations ({citations.length})
                  </button>
                  {citationsOpen && (
                    <ol className="mt-3 space-y-1 text-sm">
                      {citations.map((c, i) => {
                        const openable = resolveCitationDoc(c) !== null;
                        return (
                          <li key={i}>
                            <button
                              onClick={() => openable && handleCitationClick(c)}
                              disabled={!openable}
                              className={`w-full text-left rounded px-2 py-1 transition-colors ${
                                openable
                                  ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer text-secondary dark:text-dark-text'
                                  : 'text-accent dark:text-dark-muted cursor-not-allowed'
                              }`}
                              title={openable ? `Open ${c.docName} at page ${c.page}` : 'Source document is not available'}
                            >
                              <span className="text-accent dark:text-dark-muted mr-2">[{i + 1}]</span>
                              <span className={openable ? 'text-primary underline decoration-dotted' : ''}>
                                {c.docName}
                              </span>
                              <span className="text-accent dark:text-dark-muted"> — p. {c.page}</span>
                              {c.section ? <span className="text-accent dark:text-dark-muted"> · {c.section}</span> : null}
                              {c.quote ? (
                                <div className="text-xs italic text-accent dark:text-dark-muted mt-0.5 pl-6 line-clamp-2">
                                  "{c.quote}"
                                </div>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  {citationsOpen && citationOpenable < citations.length && (
                    <p className="mt-2 text-xs text-accent dark:text-dark-muted">
                      {citations.length - citationOpenable} citation(s) reference documents that aren't linked to this summary and can't be opened.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {citationViewer && (
        <PdfViewer
          fileUrl={`${apiBase}/api/documents/${citationViewer.documentId}/stream`}
          initialPage={citationViewer.page}
          onClose={() => setCitationViewer(null)}
        />
      )}
    </div>
  );
};

export default InsuranceSummaryDetail;
